/**
 * App-tier auth (Phase 2 multi-user dashboard).
 * Bearer token persisted in localStorage under `app_token` (separate from
 * `staff_token`, which the Staff PWA uses).
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'app_token';

export const appApi = axios.create({ baseURL: `${API}/api` });

appApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(STORAGE_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export function setAppToken(t) {
  if (t) localStorage.setItem(STORAGE_KEY, t);
  else localStorage.removeItem(STORAGE_KEY);
}
export function getAppToken() { return localStorage.getItem(STORAGE_KEY); }

// Auth (shared backend, app-token storage)
export const appLogin = (email, password) => appApi.post('/auth/login', { email, password }).then((r) => r.data);
export const appRegister = (name, email, password, pin) => appApi.post('/auth/register', { name, email, password, pin }).then((r) => r.data);
export const appValidatePin = (pin) => appApi.post('/auth/validate-pin', { pin }).then((r) => r.data);
export const appMe = () => appApi.get('/auth/me').then((r) => r.data);
export const appLogout = () => appApi.post('/auth/logout').then((r) => r.data).catch(() => null);
export const appForgot = (email) => appApi.post('/auth/forgot-password', { email }).then((r) => r.data);
export const appReset = (token, new_password) => appApi.post('/auth/reset-password', { token, new_password }).then((r) => r.data);
export const appAdminPassword = () => appApi.get('/auth/admin-password').then((r) => r.data);

// App-tier admin endpoints
export const appAdminListPages = () => appApi.get('/app-admin/pages').then((r) => r.data);
export const appAdminListUsers = () => appApi.get('/app-admin/users').then((r) => r.data);
export const appAdminCreateInvite = (payload) => appApi.post('/app-admin/invites', payload).then((r) => r.data);
export const appAdminUpdateUser = (userId, payload) => appApi.patch(`/app-admin/users/${userId}`, payload).then((r) => r.data);
export const appAdminRevokeUser = (userId) => appApi.delete(`/app-admin/users/${userId}`).then((r) => r.data);
export const appAdminResetUserPassword = (userId) => appApi.post(`/app-admin/users/${userId}/reset-password`).then((r) => r.data);
export const appAdminRevokeInvite = (inviteId) => appApi.delete(`/app-admin/invites/${inviteId}`).then((r) => r.data);

// Activity logs (admin)
export const appAdminListActivityLogs = (params = {}) =>
  appApi.get('/admin/activity-logs', { params }).then((r) => r.data);
export const appAdminActivityFacets = () =>
  appApi.get('/admin/activity-logs/facets').then((r) => r.data);

export function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}
